/**
 * Вкладка «Голоса» «Голосового стенда» (`?tab=voices`): одна фраза робота каждым голосом OpenAI, Gemini и Яндекса —
 * послушать и «Выбрать» (модель и голос уходят в форму разговора). Образцы — `scripts.voice_lab.samples`
 * (`storage/voice/samples/`), синтез обычными TTS-моделями тех же голосов: тембр тот же, интонация в разговоре
 * может отличаться, по телефону звук уже. `GET /api/voice-lab/voices`.
 */
import { useQuery } from "@tanstack/react-query";

import { type LabVoiceSample, labVoiceUrl, labVoicesQuery } from "@/lib/voiceLab";

const GROUPS: [LabVoiceSample["provider"], string][] = [
  ["openai", "OpenAI gpt-realtime"],
  ["google", "Gemini Live"],
  ["yandex", "Яндекс Speech Realtime"],
];

export function VoiceLabVoices({ chosen, pick }: { chosen: { model: string; voice: string }; pick: (model: string, voice: string) => void }) {
  const q = useQuery(labVoicesQuery());
  const voices = q.data?.voices ?? [];
  return (
    <section className="flex max-w-3xl flex-col gap-3 text-[12.5px]" data-annot="голоса">
      {q.data?.phrase && <div className="text-fg-muted">Одна фраза каждым голосом: «{q.data.phrase}»</div>}
      <div className="text-[12px] text-fg-muted">
        Синтез обычными моделями тех же голосов — тембр как у робота, интонация в разговоре может отличаться. По телефону звук уже (8 кГц), акцент
        слышнее.
      </div>
      {q.isError && <div className="text-err">Образцы не загрузились</div>}
      {!q.isLoading && !voices.length && <div className="text-fg-muted">Образцов нет — сгенерировать: scripts.voice_lab.samples</div>}
      {GROUPS.map(([provider, title]) => {
        const items = voices.filter((v) => v.provider === provider);
        if (!items.length) return null;
        return (
          <div key={provider} className="flex flex-col">
            <div className="mb-1 mt-2 text-[13px] font-semibold">{title}</div>
            {items.map((v) => {
              const on = chosen.model === v.model && chosen.voice === v.voice;
              return (
                <div key={v.file} className="flex flex-wrap items-center gap-3 border-b border-border py-1.5">
                  <span className={`w-28 shrink-0 ${on ? "font-semibold text-accent" : ""}`}>{v.voice}</span>
                  <audio controls preload="none" src={labVoiceUrl(v.file)} className="h-8 min-w-0 flex-1" />
                  <button
                    type="button"
                    onClick={() => pick(v.model, v.voice)}
                    className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12px] ${on ? "border-accent text-accent" : "border-border text-fg-muted hover:text-fg"}`}
                  >
                    {on ? "Выбран" : "Выбрать"}
                  </button>
                </div>
              );
            })}
          </div>
        );
      })}
    </section>
  );
}

/** Вкладка «Марин»: голос marin самой разговорной моделью на скоростях 1,0–1,2 и в двух написаниях бренда. */
export function VoiceLabMarin({ chosen, pick }: { chosen: number | null; pick: (speed: number) => void }) {
  const q = useQuery(labVoicesQuery());
  const samples = q.data?.marin.samples ?? [];
  return (
    <section className="flex max-w-3xl flex-col gap-2 text-[12.5px]" data-annot="марин скорости">
      {q.data?.marin.phrase && <div className="text-fg-muted">«{q.data.marin.phrase}»</div>}
      <div className="text-[12px] text-fg-muted">
        Озвучено самой gpt-realtime-2.1, как в звонке; скорость — параметр сессии (ускорение готового звука). Паузы модель расставляет каждый раз
        чуть по-разному. Бренд — слитно «Юнирентер» и двумя словами «Юни Рентер».
      </div>
      {!q.isLoading && !samples.length && <div className="text-fg-muted">Образцов нет — scripts.voice_lab.samples --marin</div>}
      {samples.map((x) => {
        const on = chosen === x.speed;
        return (
          <div key={x.file} className="flex flex-wrap items-center gap-3 border-b border-border py-1.5">
            <span className={`w-40 shrink-0 ${on ? "font-semibold text-accent" : ""}`}>
              ×{x.speed.toFixed(2)} · <span className="text-fg-muted">{x.brand}</span>
            </span>
            <audio controls preload="none" src={labVoiceUrl(x.file)} className="h-8 min-w-0 flex-1" />
            <button
              type="button"
              onClick={() => pick(x.speed)}
              className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12px] ${on ? "border-accent text-accent" : "border-border text-fg-muted hover:text-fg"}`}
            >
              {on ? "Выбрана" : "Выбрать"}
            </button>
          </div>
        );
      })}
    </section>
  );
}
